import { useNotifications } from '@/providers/notifications';
import { useMedia } from '@core/hooks/use-media';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Link } from 'react-router-dom';
import { ReactElement, RefObject, useState } from 'react';
import { PiBellSimpleSlashDuotone, PiChecksBold } from 'react-icons/pi';
import { Badge, Popover, Text, Title } from 'rizzui';
import cn from '@core/utils/class-names';
import { NotificationIcon } from '@/domain/notification-icon';

dayjs.extend(relativeTime);

/**
 * Non-mechanical edit, the same kind `header-menu-right.tsx` records: the template listed a
 * module-level demo array with a "Mark all as read" checkbox wired to nothing and a "View All
 * Activity" link to `#`. The list and its actions now read the host's inbox through
 * `providers/notifications.tsx`, on the template's row layout.
 *
 * 2026-09-28, the Marketplace admin portal's dropdown: each row's icon is its event's own
 * (`NotificationIcon` — an order awaiting approval, a refused refund, a silent Tally agent), tinted
 * by severity; an unread row is tinted and bold; the message wraps to two lines instead of being
 * cut off; "Mark all as read" is a small tinted button (it was a checkbox that read like a
 * setting); the header carries the unread count.
 */
function NotificationsList({
  setIsOpen,
}: {
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { items, unreadCount, error, markRead, markAllRead, viewAllHref } = useNotifications();

  return (
    <div className="w-[340px] text-left sm:w-[380px] 2xl:w-[420px] rtl:text-right">
      <div className="mb-2 flex items-center justify-between gap-3 border-b border-muted px-5 pb-3">
        <div className="flex items-center gap-2">
          <Title as="h5" fontWeight="semibold" className="text-base">
            Notifications
          </Title>
          {unreadCount > 0 ? (
            <Badge variant="flat" color="primary" size="sm" className="tabular-nums">
              {unreadCount} new
            </Badge>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => void markAllRead()}
          disabled={unreadCount === 0}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2.5 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/15 disabled:bg-transparent disabled:text-gray-400"
        >
          <PiChecksBold aria-hidden className="size-3.5" />
          Mark all as read
        </button>
      </div>
      <div className="custom-scrollbar max-h-[440px] overflow-y-auto scroll-smooth">
        {error ? (
          <Text className="px-6 py-8 text-center text-sm text-red-dark" role="alert">{error}</Text>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
            <PiBellSimpleSlashDuotone aria-hidden className="size-8 text-gray-400" />
            <Text className="text-sm text-gray-500">You&rsquo;re all caught up — no notifications.</Text>
          </div>
        ) : (
        <div className="grid grid-cols-1 gap-0.5 px-2">
          {items.map((item) => {
            const open = () => {
              if (!item.read) void markRead(item.id);
              setIsOpen(false);
            };
            return (
            <Link
              key={item.id}
              to={item.link ?? viewAllHref}
              onClick={open}
              className={cn(
                'group grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-50',
                !item.read && 'bg-primary/[0.04]',
              )}
            >
              <NotificationIcon kind={item.kind} severity={item.severity} />
              <div className="min-w-0">
                <Text className={cn('text-sm leading-5 text-gray-900 dark:text-gray-700', item.read ? 'font-medium' : 'font-semibold')}>
                  {item.title}
                </Text>
                {item.body ? (
                  <Text className="mt-0.5 line-clamp-2 text-xs leading-snug text-gray-600" title={item.body}>
                    {item.body}
                  </Text>
                ) : null}
                <Text className="mt-1 text-xs text-gray-500">
                  {dayjs(item.occurredAt).fromNow()}
                </Text>
              </div>
              {!item.read ? (
                <span className="mt-1.5 size-2 rounded-full bg-primary" aria-label="Unread" />
              ) : (
                <span className="sr-only">Read</span>
              )}
            </Link>
            );
          })}
        </div>
        )}
      </div>
      <Link
        to={viewAllHref}
        onClick={() => setIsOpen(false)}
        className="mx-5 mt-2 block border-t border-muted pt-3 text-center text-sm font-medium text-primary hover:underline"
      >
        View all notifications
      </Link>
    </div>
  );
}

export default function NotificationDropdown({
  children,
}: {
  children: ReactElement & { ref?: RefObject<any> };
}) {
  const isMobile = useMedia('(max-width: 480px)', false);
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Popover
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      shadow="sm"
      placement={isMobile ? 'bottom' : 'bottom-end'}
    >
      <Popover.Trigger>{children}</Popover.Trigger>
      <Popover.Content className="z-[9999] px-0 pb-4 pt-4 dark:bg-gray-100 [&>svg]:hidden [&>svg]:dark:fill-gray-100 sm:[&>svg]:inline-flex">
        <NotificationsList setIsOpen={setIsOpen} />
      </Popover.Content>
    </Popover>
  );
}
